/* The exercise card's progression tile — `Builder planu - tydzien - programator`'s foot, left column.
 *
 * The mock: `progBox`, a 22px row at radius 6 on a hairline, `0 8px`, gap 6, holding a 9px tracked
 * uppercase `Progresja`, a 17px kind chip, a spring, and an 18px pencil at the right end.
 *
 * ── It is the mock's ROW again, and it is sized to its CONTENTS (8 September 2026, second pass) ──
 * The 8 September round answered *"by przycisk progresja był ładniejszy i bardziej widoczny"* by
 * doubling the row to `--h-card-foot` and raising it, and it overshot in the one dimension that
 * could not carry it. The tile had no pencil then — there was no rule editor to open — so the
 * mock's own spring had nothing to push against, and a 44px box stretched across a `1fr` grid track
 * with two words at its left end and half its width empty is what the owner then read as *"mega
 * mierna … tak sztucznie wielki i suchy"*. Both halves of that are the same fault: the tile was
 * claiming room for a control it does not have.
 *
 * So the height is the mock's 22px again and the width is the tile's own — `plan-day-programming`'s
 * foot gives this slot an `auto` track now, and every pixel it gave up went to the coaching note
 * beside it, which is a real field a trainer types two sentences into. Measured in the 574px panel:
 * the tile went from 226px to 152px and the note from 293px to 367px.
 *
 * What is KEPT from the 8 September round is the treatment, because the reasoning behind it still
 * holds at 22px: `--bg-card` over `--border-2` under the neutral `--shadow-card`, and the label at
 * `--text-2`. On `--bg-inset` — the mock's own fill — the one thing in the foot with a box read as a
 * sunken continuation of the set table directly above it, and `--text-3` is the ink this app uses
 * for something it is not offering. The chip takes the inset fill the row gave up, so the two still
 * separate.
 *
 * Everything here is neutral: no accent and no coloured shadow. The accent is a scalpel and a
 * statement of which axes carry a rule is not one of its four marks per screen; the glow ban is
 * absolute outside `shared/ui/emberButton`.
 *
 * ── The PENCIL is back (14 September 2026), and it is the row's only press ───────────────────
 * The mock ends the row with a pencil into the rule editor, and from 15 August there was no editor
 * to open, so the tile carried no press at all. `ProgressionSheet` is that editor now, so the glyph
 * is drawn again — into a box that was already the mock's shape and had room for it. The spring is
 * still not drawn: the tile is sized to its contents, so there is nothing to push against.
 *
 * What this tile does NOT carry, and why, is in `DayProgressionSlots.tsx`. */

.row {
  align-self: start;
  display: flex;
  gap: var(--space-2);
  align-items: center;
  min-width: 0;
  height: 22px;
  padding: var(--space-0) var(--space-2);
  overflow: hidden;
  background: var(--bg-card);
  border: var(--border-hairline) solid var(--border-2);
  border-radius: var(--radius-xs);
  box-shadow: var(--shadow-card);
}

/* `--text-2`, not `--text-3`: the word names what the tile is about, and the tertiary ink is what
 * this app uses for something it is not offering. 10px is the 12 August 2026 audit's floor. */
.label {
  flex: none;
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-bold);
  letter-spacing: var(--ls-tag);
  text-transform: uppercase;
  color: var(--text-2);
  white-space: nowrap;
}

/* One stored rule: the kind chip, and the measurement field it writes, side by side — the mock's
 * `progKind` followed by its `progRule`. An exercise carries one of these in practice and may carry
 * more, so the row clips and the whole sentence is on `title` and in the hidden copy beside it. */
.axis {
  display: inline-flex;
  flex: 0 1 auto;
  gap: var(--space-1);
  align-items: center;
  min-width: 0;
}

/* The mock's `progRule` — the axis, in words, at reading ink beside the chip that colours it. */
.axisField {
  min-width: 0;
  overflow: hidden;
  font-size: var(--fs-label-sm);
  font-weight: var(--fw-semibold);
  color: var(--text-2);
  text-overflow: ellipsis;
  white-space: nowrap;
}

/* The mock's `progKind`. Neutral is the BASE — what a kind with no colour of its own takes, and what
 * "Bez progresji" takes, since the absence of a rule is not one of the mock's three kinds. The two
 * hues the wire can actually produce are merged onto it below, exactly as `PROGC` does. */
.chip {
  display: inline-flex;
  flex: 0 1 auto;
  align-items: center;
  min-width: 0;
  height: 17px;
  padding: var(--space-0) var(--space-7px);
  overflow: hidden;
  font-size: var(--fs-badge);
  font-weight: var(--fw-extrabold);
  letter-spacing: var(--ls-group);
  text-transform: uppercase;
  color: var(--text);
  text-overflow: ellipsis;
  white-space: nowrap;
  background: var(--bg-inset);
  border: var(--border-hairline) solid var(--border);
  border-radius: var(--radius-5px);
}

/* `PROGC`, value for value: dynamiczna zielen, statyczna blekit. The third hue — bursztynowa
 * manualna — is deliberately absent; see the component. Neither is the accent, and neither is a
 * glow: a flat wash under words, which is what the mock draws. */
.chip[data-tone="dynamic"] {
  color: var(--prog-kind-dynamic-ink);
  background: var(--prog-kind-dynamic-bg);
  border-color: transparent;
}

.chip[data-tone="static"] {
  color: var(--prog-kind-static-ink);
  background: var(--prog-kind-static-bg);
  border-color: transparent;
}

/* The mock's 18px pencil at the row's right end, back on 14 September 2026 with the editor behind it.
 *
 * It is the ONE press in this row, so it is the only thing here with a hover and a focus ring, and
 * it stays NEUTRAL: opening an editor is a navigation, not one of the screen's four accent marks.
 *
 * No hit extension, deliberately. `.row` clips (`overflow: hidden`), so an inset pseudo-element
 * reaching outside the 22px tile would be cut off in exactly the direction it was reaching — a hit
 * area that measures larger in the stylesheet and not on screen is worse than none. The glyph's own
 * 16px box inside a 22px row is what a pointer gets.
 */
.pencil {
  display: grid;
  flex: none;
  place-items: center;
  width: var(--space-4);
  height: var(--space-4);
  padding: var(--space-0);
  color: var(--text-2);
  cursor: pointer;
  background: transparent;
  border: none;
  border-radius: var(--radius-4px);
}

.pencil:hover {
  color: var(--text);
  background: var(--bg-inset);
}

.pencil:focus-visible {
  outline: var(--border-focus) solid var(--focus);
  outline-offset: var(--space-2px);
}

.pencil svg {
  display: block;
  width: var(--space-3);
  height: var(--space-3);
}

/* ── A change under review (`RuleChange`, board U) ───────────────────────────────────────────
 * The same tile, read-only: no pencil, and the rule the exercise had struck before the one it gets.
 * It grows a line rather than clipping when the two do not fit side by side. */
.row[data-change] {
  flex-wrap: wrap;
  height: auto;
  min-height: 22px;
}

.change {
  display: inline-flex;
  flex-wrap: wrap;
  gap: var(--space-2);
  align-items: center;
  min-width: 0;
}

.previous {
  display: inline-flex;
  gap: var(--space-1);
  align-items: center;
  color: var(--text-2);
  text-decoration: line-through;
}
